import { createAppRequest } from '@/services/request';
import { getFamsHierarchyAllocationAssetSegments, getFamsHierarchyAllocationDeviation } from './hierarchy-allocation.api';
import { getFamsHierarchyAnalyticsReturnAttribution } from './hierarchy-analytics.api';
import type { ApiRequestOptions } from './types/common';
import type {
  FamsRiskAlertItem,
  FamsRiskAlertQueryParams,
  FamsRiskAlertsOverviewResponse,
} from './types/risk-alerts';

const request = createAppRequest();

const buildTrend = (list: FamsRiskAlertItem[]) => {
  const today = new Date();
  const points: FamsRiskAlertsOverviewResponse['trend'] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const date = d.toISOString().slice(0, 10);
    const total = Math.max(0, Math.round(list.length * (0.7 + (6 - i) * 0.05)));
    const high = Math.round(total * 0.28);
    const open = Math.round(total * 0.46);
    points.push({ date, total, high, open });
  }
  return points;
};

const filterByQuery = (list: FamsRiskAlertItem[], query?: FamsRiskAlertQueryParams) => {
  const keyword = query?.keyword?.trim().toLowerCase();
  return list.filter((item) => {
    if (query?.severity && item.severity !== query.severity) return false;
    if (query?.status && item.status !== query.status) return false;
    if (!keyword) return true;
    return (
      item.alertCode.toLowerCase().includes(keyword) ||
      item.objectName.toLowerCase().includes(keyword) ||
      item.level1Label.toLowerCase().includes(keyword) ||
      item.message.toLowerCase().includes(keyword)
    );
  });
};

const summarize = (list: FamsRiskAlertItem[]) => ({
  total: list.length,
  high: list.filter((i) => i.severity === 'HIGH').length,
  processing: list.filter((i) => i.status === 'OPEN' || i.status === 'PROCESSING').length,
  closed: list.filter((i) => i.status === 'CLOSED').length,
  totalMarketValue: list.reduce((sum, i) => sum + i.marketValue, 0),
});

const distributionFromList = (list: FamsRiskAlertItem[]) => {
  const map = new Map<string, number>();
  for (const item of list) {
    map.set(item.alertType, (map.get(item.alertType) || 0) + 1);
  }
  return Array.from(map.entries()).map(([label, count], idx) => ({
    key: `dist-${idx}`,
    label,
    count,
  }));
};

const fromDeviation = async (query?: FamsRiskAlertQueryParams): Promise<FamsRiskAlertItem[]> => {
  const res = await getFamsHierarchyAllocationDeviation({ ...query, pageSize: 300 });
  return res.list.map((item, index) => {
    const ratio = Math.abs(item.gapRatio) / Math.max(item.toleranceRatio, 0.0001);
    return {
      key: item.key,
      alertCode: `RA-${String(index + 1).padStart(4, '0')}`,
      alertType: item.exceeded ? '偏离超限' : '偏离提示',
      objectType: item.objectType,
      objectName: item.title,
      level1Label: item.level1Label,
      marketValue: item.marketValue,
      actualRatio: item.actualRatio,
      targetRatio: item.targetRatio,
      gapRatio: item.gapRatio,
      toleranceRatio: item.toleranceRatio,
      severity: ratio >= 1.5 ? 'HIGH' : ratio >= 1 ? 'MEDIUM' : 'LOW',
      status: item.exceeded ? 'OPEN' : 'CLOSED',
      triggerTime: new Date().toISOString().slice(0, 10),
      message: `${item.level1Label}偏离 ${item.gapRatio.toFixed(4)}`,
    };
  });
};

const fromLookThrough = async (query?: FamsRiskAlertQueryParams): Promise<FamsRiskAlertItem[]> => {
  const res = await getFamsHierarchyAllocationAssetSegments({ ...query, viewMode: 'OBJECT', pageSize: 300 });
  return res.objectItems.map((item, index) => ({
    key: item.key,
    alertCode: `LTR-${String(index + 1).padStart(4, '0')}`,
    alertType: '穿透暴露异常',
    objectType: item.objectType,
    objectName: item.title,
    level1Label: item.assetBucketLabel,
    marketValue: item.marketValue,
    actualRatio: item.ratio,
    targetRatio: 0,
    gapRatio: item.ratio,
    toleranceRatio: 0.1,
    severity: item.ratio > 0.2 ? 'HIGH' : item.ratio > 0.1 ? 'MEDIUM' : 'LOW',
    status: item.ratio > 0.1 ? 'OPEN' : 'CLOSED',
    triggerTime: new Date().toISOString().slice(0, 10),
    message: `${item.assetBucketLabel}穿透占比 ${item.ratio.toFixed(4)}`,
  }));
};

const fromReturn = async (query?: FamsRiskAlertQueryParams): Promise<FamsRiskAlertItem[]> => {
  const res = await getFamsHierarchyAnalyticsReturnAttribution({ ...query, rankBy: 'TOTAL_RETURN', pageSize: 300 });
  return (res.list || []).map((item, index) => ({
    key: `${item.nodeKey || item.projectRecordId || index}`,
    alertCode: `RTN-${String(index + 1).padStart(4, '0')}`,
    alertType: '收益异常',
    objectType: item.objectType,
    objectName: item.title,
    level1Label: item.parentTitle || 'PKU',
    marketValue: item.marketValue,
    actualRatio: item.returnRate,
    targetRatio: 0,
    gapRatio: item.totalReturn,
    toleranceRatio: 0.08,
    severity: Math.abs(item.totalReturn) > 6e7 ? 'HIGH' : Math.abs(item.totalReturn) > 2e7 ? 'MEDIUM' : 'LOW',
    status: Math.abs(item.totalReturn) > 2e7 ? 'OPEN' : 'CLOSED',
    triggerTime: new Date().toISOString().slice(0, 10),
    message: `收益贡献 ${item.totalReturn.toFixed(2)}`,
  }));
};

const fetchFallback = async (query?: FamsRiskAlertQueryParams) => {
  if (query?.scene === 'LOOK_THROUGH_RISK' || query?.scene === 'HIERARCHY_LOOK_THROUGH') {
    return fromLookThrough(query);
  }
  if (
    query?.scene === 'HIERARCHY_RISK_ATTRIBUTION' ||
    query?.scene === 'HIERARCHY_CHANGE_ANALYSIS' ||
    query?.scene === 'PROJECT_ANALYSIS'
  ) {
    return fromReturn(query);
  }
  return fromDeviation(query);
};

export async function getFamsRiskAlertsOverview(
  params?: FamsRiskAlertQueryParams,
  options?: ApiRequestOptions,
): Promise<FamsRiskAlertsOverviewResponse> {
  try {
    return await request<FamsRiskAlertsOverviewResponse>('/bff/fams/risk-alerts/overview', {
      method: 'GET',
      params,
      ...(options || {}),
    });
  } catch {
    const rawList = await fetchFallback(params);
    const list = filterByQuery(rawList, params);
    const summary = summarize(list);
    return {
      snapshotDate: params?.snapshotDate || null,
      summary,
      distribution: distributionFromList(list),
      trend: buildTrend(list),
      list,
      total: list.length,
      current: params?.current || 1,
      pageSize: params?.pageSize || list.length || 10,
    };
  }
}
